import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ClassesContext from '../../context/ClassesContext';
import AacnContext from '../../context/AacnContext';
import { getCurrentUserEmail } from '../../utils/currentUser';
import { splitRecordings, sessionRecordingText } from '../../utils/transcriptRecordings';
import './StudentDetail.css';
import Footer from '../Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';

// Every backend request costs ~1s regardless of size, so Prev/Next would wait a
// full round trip per student. Detail responses are cached briefly (long enough
// for paging through a class, short enough that new sessions show up soon) and
// the neighbours of the current student are prefetched into this cache. Entries
// hold the promise, so a click on a student still being prefetched reuses it.
const STUDENT_DETAIL_TTL_MS = 60 * 1000;
const studentDetailCache = new Map(); // studentId -> { at, promise }

function loadStudentDetail(studentId) {
  const hit = studentDetailCache.get(studentId);
  if (hit && Date.now() - hit.at < STUDENT_DETAIL_TTL_MS) return hit.promise;
  const entry = { at: Date.now(), promise: null };
  entry.promise = axios.get(`${BACKEND_URL}/api/students/${studentId}`).then((res) => res.data);
  entry.promise.catch(() => {
    if (studentDetailCache.get(studentId) === entry) studentDetailCache.delete(studentId);
  });
  studentDetailCache.set(studentId, entry);
  return entry.promise;
}

// The detail endpoint doesn't include `tag` — only the full roster does — so the
// roster is fetched (once per TTL, not once per student) just to look tags up.
const ROSTER_TTL_MS = 5 * 60 * 1000;
let rosterCache = null; // { at, promise }

function loadStudentTag(studentId) {
  if (!rosterCache || Date.now() - rosterCache.at >= ROSTER_TTL_MS) {
    const entry = { at: Date.now(), promise: null };
    entry.promise = axios.get(`${BACKEND_URL}/api/students`).then((res) => (Array.isArray(res.data) ? res.data : []));
    entry.promise.catch(() => { if (rosterCache === entry) rosterCache = null; });
    rosterCache = entry;
  }
  return rosterCache.promise.then((roster) => roster.find((s) => s.id === studentId)?.tag || null);
}

// Grading is an AI call per session — cache results in localStorage so revisiting a
// student's page doesn't re-grade every session again. Keyed by session id, with the
// case's target sub-competency codes recorded alongside so a cache entry is ignored
// (rather than shown stale) if the case's picked indicators ever change. v2: grades
// are scoped to the session's own recording (see transcriptRecordings.js); v1
// entries were graded on the whole cumulative transcript, so they're abandoned.
const GRADE_CACHE_KEY = 'kabilah_indicator_grade_cache_v2';

function loadGradeCache() {
  try {
    return JSON.parse(localStorage.getItem(GRADE_CACHE_KEY)) || {};
  } catch {
    return {};
  }
}

function getCachedGrade(sessionId, codesKey) {
  const entry = loadGradeCache()[sessionId];
  return entry && entry.codesKey === codesKey
    ? { levels: entry.levels, indicatorResults: entry.indicatorResults || {} }
    : null;
}

function setCachedGrade(sessionId, codesKey, levels, indicatorResults) {
  try {
    const cache = loadGradeCache();
    cache[sessionId] = { codesKey, levels, indicatorResults };
    localStorage.setItem(GRADE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Caching is a perf optimization, not a correctness requirement — ignore storage errors.
  }
}

function ChecklistItem({ text, addressed }) {
  return (
    <div className="checklist-item">
      <div className={`checklist-circle ${addressed ? 'checklist-circle-done' : 'checklist-circle-open'}`} />
      <span className={`checklist-text ${addressed ? 'checklist-text-done' : ''}`}>{text}</span>
    </div>
  );
}

const INDICATOR_LEVEL_LABELS = {
  developed: 'Developed',
  developing: 'Developing',
  none: 'Not Yet Evidenced',
};

function IndicatorBreakdownRow({ grade, indicator }) {
  const level = grade?.level;
  const label = INDICATOR_LEVEL_LABELS[level] || 'Not graded';
  // Show the rubric text for the level that was actually reached — for "none"/"not
  // graded" there's no single matching line, so show both as reference for what
  // was being looked for.
  const lines = level === 'developed'
    ? [{ tag: 'Developed', text: indicator?.developed }]
    : level === 'developing'
      ? [{ tag: 'Developing', text: indicator?.developing }]
      : [
          { tag: 'Developing', text: indicator?.developing },
          { tag: 'Developed', text: indicator?.developed },
        ];

  return (
    <div className="indicator-breakdown-row">
      <div className="indicator-breakdown-row-header">
        Indicator {(grade?.index ?? 0) + 1}: <span className={`indicator-breakdown-level indicator-breakdown-level-${level || 'null'}`}>{label}</span>
      </div>
      {lines.filter((l) => l.text).map((l) => (
        <p key={l.tag} className="indicator-breakdown-text">
          <span className="indicator-breakdown-text-tag">{l.tag}:</span> "{l.text}"
        </p>
      ))}
    </div>
  );
}

// level: 'developed' | 'developing' | 'none' | undefined (grading in flight, no per-chip label) | 'error'
function IndicatorChip({ subCompetency, level, indicatorGrades }) {
  const statusLabel = level === 'developed' ? 'Developed'
    : level === 'developing' ? 'Developing'
    : level === 'none' ? 'Not Yet Evidenced'
    : level === 'error' ? 'Could not grade'
    : null;
  const hasBreakdown = Array.isArray(indicatorGrades) && indicatorGrades.length > 0;
  const wrapRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);

  // The breakdown panel is portalled to <body> instead of living inside the
  // chip's own DOM position — session cards clip overflowing content
  // (rounded-corner masking), which was cutting the panel off whenever a
  // chip sat near the card's edge. Position is computed from the chip's own
  // bounding box so it still visually anchors to it.
  const showPanel = () => {
    if (!hasBreakdown || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const halfHeight = 160;
    const centerY = Math.min(
      Math.max(rect.top + rect.height / 2, halfHeight + 8),
      window.innerHeight - halfHeight - 8
    );
    setPanelPos({ top: centerY, right: window.innerWidth - rect.left + 10 });
  };
  const hidePanel = () => setPanelPos(null);

  return (
    <span
      ref={wrapRef}
      className="indicator-chip-wrap"
      onMouseEnter={showPanel}
      onMouseLeave={hidePanel}
    >
      <span
        className={`indicator-chip ${statusLabel ? `indicator-chip-${level}` : ''}`}
        title={hasBreakdown ? undefined : `${subCompetency.subCompetencyCode} — ${subCompetency.subCompetency}${statusLabel ? ` (${statusLabel})` : ''}`}
      >
        <span className="indicator-chip-text">
          <span className="indicator-chip-code">{subCompetency.subCompetencyCode}</span> {subCompetency.subCompetency}
        </span>
        {statusLabel && <span className="indicator-chip-status-tag">{statusLabel}</span>}
      </span>
      {hasBreakdown && panelPos && ReactDOM.createPortal(
        <div
          className="indicator-breakdown-panel"
          style={{ top: panelPos.top, right: panelPos.right }}
          onMouseEnter={showPanel}
          onMouseLeave={hidePanel}
        >
          <div className="indicator-breakdown-title">
            <span className="indicator-chip-code">{subCompetency.subCompetencyCode}</span> {subCompetency.subCompetency}
          </div>
          {indicatorGrades.map((grade) => (
            <IndicatorBreakdownRow
              key={grade.index}
              grade={grade}
              indicator={subCompetency.indicators?.[grade.index]}
            />
          ))}
        </div>,
        document.body
      )}
    </span>
  );
}

// A session's transcript is cumulative (see transcriptRecordings.js), but only
// its own, last recording belongs to it: earlier ones are their own sessions.
// Show just that recording, formatted like any single-recording transcript
// (timestamp line, then the text), so the box matches what the checklist and
// AACN indicators beside it evaluate.
function SessionTranscript({ transcript }) {
  const recordings = splitRecordings(transcript);
  if (recordings.length <= 1) {
    return <p className="session-transcript-text">{transcript}</p>;
  }
  const { recordedAt, text } = recordings[recordings.length - 1];
  return <p className="session-transcript-text">{recordedAt ? `${recordedAt}\n${text}` : text}</p>;
}

function SessionCard({ session, targetSubCompetencies, gradingResult, onRegrade }) {
  const { patient, timestamp, transcript, checklist } = session;
  const isGrading = !gradingResult || gradingResult === 'pending';
  const levels = gradingResult && !isGrading && gradingResult !== 'error' ? gradingResult.levels : {};
  const indicatorResultsByCode = gradingResult && !isGrading && gradingResult !== 'error' ? gradingResult.indicatorResults : {};
  const chipLevel = (code) => (isGrading ? undefined : gradingResult === 'error' ? 'error' : levels[code]);

  return (
    <div className="session-card">
      <div className="session-card-header">
        <span className="session-patient-link">
          Generated for {patient.name}
        </span>
        <span className="session-patient-info">
          ({patient.gender}, {patient.age}, Room {patient.room}, Admitted: {patient.admitDate}, {patient.doctor}{patient.mrn ? `, MRN ${patient.mrn}` : ''})
        </span>
        <span className="session-timestamp">on {timestamp}</span>
      </div>

      <div className="session-content">
        <div className="session-columns">
          <div className="session-transcript-col">
            <SessionTranscript transcript={transcript} />
          </div>
          <div className="session-divider">→</div>
          <div className="session-checklist-col">
            {checklist.map((item, i) => (
              <ChecklistItem key={i} text={item.text} addressed={item.addressed} />
            ))}
          </div>
          {targetSubCompetencies.length > 0 && (
            <div className="session-indicators-col">
              <div className="session-indicators-label">
                AACN Indicators
                {isGrading && <span className="session-indicators-grading">Grading…</span>}
                <button
                  className="session-indicators-regrade"
                  onClick={onRegrade}
                  disabled={isGrading}
                  title="Regrade this recording's AACN indicators"
                >
                  ↻ Regrade
                </button>
              </div>
              <div className="session-indicators-row">
                {targetSubCompetencies.map((sc) => (
                  <IndicatorChip
                    key={sc.subCompetencyCode}
                    subCompetency={sc}
                    level={chipLevel(sc.subCompetencyCode)}
                    indicatorGrades={indicatorResultsByCode?.[sc.subCompetencyCode]}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  // Set by ClassDetail when opened via a class roster's "View": the class to go
  // back to, that roster's students (in the order shown) for Prev/Next, and the
  // case to land on. Absent when opened from the Students page or directly.
  const { fromClass, studentList, caseId: preferredCaseId } = useLocation().state || {};
  const { classes } = useContext(ClassesContext);
  const { getSubCompetencyByCode } = useContext(AacnContext);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);
  const [gradingState, setGradingState] = useState({}); // sessionId -> 'pending' | 'error' | { levels }
  const [studentTag, setStudentTag] = useState(null);

  useEffect(() => {
    // Prev/Next changes studentId without remounting, so reset per-student state
    // and ignore responses for a student we've already moved past.
    let cancelled = false;
    setStudent(null);
    setLoading(true);
    setStudentTag(null);

    loadStudentDetail(studentId)
      .then((data) => {
        if (cancelled || !data) return;
        const preferredIndex = (data.cases || []).findIndex((c) => c.caseId === preferredCaseId);
        setActiveCaseIndex(preferredIndex >= 0 ? preferredIndex : 0);
        setStudent(data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    loadStudentTag(studentId)
      .then((tag) => { if (!cancelled) setStudentTag(tag); })
      .catch(() => {});

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const rosterIndex = studentList ? studentList.findIndex((s) => s.id === studentId) : -1;
  const prevStudent = rosterIndex > 0 ? studentList[rosterIndex - 1] : null;
  const nextStudent = rosterIndex >= 0 && rosterIndex < studentList.length - 1 ? studentList[rosterIndex + 1] : null;

  // Warm the cache for Prev/Next only after the current student has arrived, so
  // the prefetches never compete with the request the user is waiting on.
  useEffect(() => {
    if (!student) return;
    [prevStudent, nextStudent].forEach((s) => { if (s) loadStudentDetail(s.id).catch(() => {}); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  // replace (not push) so the browser's Back still returns to the class page
  // rather than stepping back through every student viewed.
  const goToStudent = (s) => navigate(`/admin/students/${s.id}`, {
    replace: true,
    state: { fromClass, studentList, caseId: student?.cases?.[activeCaseIndex]?.caseId || preferredCaseId },
  });

  useEffect(() => {
    if (rosterIndex < 0) return;
    const onKeyDown = (e) => {
      if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.key === 'ArrowLeft' && prevStudent) goToStudent(prevStudent);
      if (e.key === 'ArrowRight' && nextStudent) goToStudent(nextStudent);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const activeCase = student?.cases?.[activeCaseIndex];

  const getCaseTargetSubCompetencies = (caseItem) => (caseItem?.indicatorCodes || [])
    .map(getSubCompetencyByCode)
    .filter(Boolean);
  const targetSubCompetencies = getCaseTargetSubCompetencies(activeCase);

  // Which codes get graded is derived server-side from the case's own indicatorCodes
  // (looked up via caseId) — this is just the cache key, not sent in the request.
  const buildCodesKey = (subCompetencies) => subCompetencies.map((sc) => sc.subCompetencyCode).sort().join(',');

  const gradeSession = (session, subCompetencies, { force = false } = {}) => {
    const codesKey = buildCodesKey(subCompetencies);
    if (!force) {
      const cached = getCachedGrade(session.id, codesKey);
      if (cached) {
        setGradingState((prev) => ({ ...prev, [session.id]: cached }));
        return Promise.resolve();
      }
    }
    const previousResult = gradingState[session.id];
    setGradingState((prev) => ({ ...prev, [session.id]: 'pending' }));
    return axios.post(`${BACKEND_URL}/api/sessions/grade-indicators`, {
      // Only this session's own recording, matching what its checklist graded.
      transcript: sessionRecordingText(session.transcript),
      studentId,
      caseId: activeCase.caseId,
      sessionId: session.id,
      // Regrade: the backend otherwise answers from its shared grading cache
      // (same transcript + rubric -> same result, no AI call), so without this
      // the button just returned the old grades. force makes it call the AI
      // again and replace the cached result; it's staff-only, hence the email.
      ...(force ? { force: true, requestingAdminEmail: getCurrentUserEmail() } : {}),
    })
      .then((res) => {
        const levels = res.data?.results || {};
        const indicatorResults = res.data?.indicatorResults || {};
        setGradingState((prev) => ({ ...prev, [session.id]: { levels, indicatorResults } }));
        setCachedGrade(session.id, codesKey, levels, indicatorResults);
      })
      .catch(() => {
        // A failed Regrade leaves the existing grades in place (the backend
        // keeps them too), rather than replacing them with an error.
        const keep = force && previousResult && typeof previousResult === 'object';
        setGradingState((prev) => ({ ...prev, [session.id]: keep ? previousResult : 'error' }));
      });
  };

  useEffect(() => {
    if (!activeCase || targetSubCompetencies.length === 0) return;
    activeCase.sessions.forEach((session) => {
      if (gradingState[session.id] || !sessionRecordingText(session.transcript)) return;
      gradeSession(session, targetSubCompetencies);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCase]);

  const handleRegradeSession = (session) => {
    if (targetSubCompetencies.length === 0 || !sessionRecordingText(session.transcript)) return;
    gradeSession(session, targetSubCompetencies, { force: true });
  };

  // While the next student loads, the header (name from the class roster,
  // back button, Prev/Next) stays up and only the body shows "Loading...".
  const displayName = student?.name || (rosterIndex >= 0 ? studentList[rosterIndex].name : '');

  const studentClasses = classes.filter((c) => (c.students || []).some((s) => s.id === studentId));

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          {fromClass ? (
            <button className="back-button" onClick={() => navigate(`/admin/classes/${fromClass.id}`)}>
              ← {fromClass.name}
            </button>
          ) : (
            <button className="back-button" onClick={() => navigate('/admin/students')}>
              ← Students
            </button>
          )}
          <div className="title-name-container" style={{ marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <header className="name-header">{displayName}</header>
            {studentTag === 'demo' && <span className="case-badge case-badge-demo">Demo</span>}
          </div>
          <div className="title-info-container">
            {studentClasses.length > 0 ? (
              <div className="student-detail-classes">
                {studentClasses.map((c) => (
                  <span key={c.id} className="student-class-chip">{c.name}</span>
                ))}
              </div>
            ) : student?.class ? (
              <button className="info-button">
                <span className="info-item">{student.class}</span>
              </button>
            ) : null}
          </div>
        </div>
        <div className="reload-button-container">
          {rosterIndex >= 0 && (
            <div className="student-pager">
              <button
                className="student-pager-btn"
                onClick={() => goToStudent(prevStudent)}
                disabled={!prevStudent}
                title={prevStudent ? `Previous: ${prevStudent.name} (←)` : undefined}
              >
                ‹ Prev
              </button>
              <span className="student-pager-count">{rosterIndex + 1} of {studentList.length}</span>
              <button
                className="student-pager-btn"
                onClick={() => goToStudent(nextStudent)}
                disabled={!nextStudent}
                title={nextStudent ? `Next: ${nextStudent.name} (→)` : undefined}
              >
                Next ›
              </button>
            </div>
          )}
          <button
            className="title-button"
            onClick={() => window.open(`${BACKEND_URL}/api/students/${studentId}/indicators/export`, '_blank')}
          >
            Export
          </button>
        </div>
      </div>

      <div className="component-container">
        {loading ? (
          <p style={{ padding: '50px' }}>Loading...</p>
        ) : !student ? (
          <p style={{ padding: '50px' }}>Student not found.</p>
        ) : (
        <div className="student-detail-content">

          <div className="case-tabs">
            {student.cases.map((c, i) => (
              <button
                key={c.caseId}
                className={`case-tab ${i === activeCaseIndex ? 'case-tab-active' : ''}`}
                onClick={() => setActiveCaseIndex(i)}
                title={c.caseName}
              >
                <span className="case-tab-name">{c.caseName}</span>
                <span className="case-tab-count">
                  {c.sessions.length} {c.sessions.length === 1 ? 'session' : 'sessions'}
                </span>
              </button>
            ))}
          </div>

          <div className="session-columns-layout">
            <div className="session-main-col">
              <div className="column-title-row">
                <span className="column-title">Transcript + Handoff Checklist</span>
              </div>
              {!activeCase ? (
                <div className="session-empty">No cases assigned to this student yet.</div>
              ) : activeCase.sessions.length > 0 ? (
                activeCase.sessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    targetSubCompetencies={targetSubCompetencies}
                    gradingResult={gradingState[session.id]}
                    onRegrade={() => handleRegradeSession(session)}
                  />
                ))
              ) : (
                <div className="session-empty">
                  No sessions recorded yet for this case.
                </div>
              )}

              {activeCase?.chartingNotes?.length > 0 && (
                <div className="session-charting-section">
                  <div className="column-title">Charting Notes</div>
                  {activeCase.chartingNotes.map((n, i) => (
                    <div key={i} className="session-card">
                      <div className="session-card-header">
                        <span className="session-timestamp">{n.timestamp}</span>
                      </div>
                      <div className="session-charting-text">{n.note}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default StudentDetail;
